import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Story 24.3 — system Back closes the topmost open layer (Sheet, Dialog,
 * AlertDialog) instead of leaving the page.
 *
 * - On open: push a sentinel history entry that copies the current
 *   `history.state` (so react-router's `idx` / `key` survive) and adds
 *   `backDismiss: <id>`. The URL does not change.
 * - One module-level `popstate` listener closes only the topmost registered
 *   layer, so stacked layers close one per Back.
 * - Closing by any other path (X, Esc, scrim, a button, a prop change) calls
 *   `history.back()` to drop the sentinel — but only when the current entry
 *   IS that sentinel. If a navigation pushed past it, nothing is undone. The
 *   `popstate` that our own `back()` causes closes nothing.
 * - StrictMode runs every effect twice on mount (mount → cleanup → mount).
 *   The cleanup defers its `back()` by a tick; a re-run inside that tick
 *   cancels it and keeps the sentinel, so there is exactly one push and no
 *   pop. The re-run reuses the sentinel only while the current entry still
 *   IS that sentinel (a Back-close and a reopen in the same tick push anew).
 *
 * Sentinel vs router writes (review loop 1). react-router writes
 * `history.replaceState` / `pushState` with its own `{usr, key, idx}` state,
 * which would drop our marker. So, installed once alongside the listener:
 *
 * - `replaceState` over a live sentinel keeps the marker and records the
 *   layer's latest URL and state (a FilterSheet's `?status=` write while
 *   open, through `useFilterParams`' `{replace: true}`).
 * - `pushState` over a live sentinel is performed as a `replaceState`
 *   (history becomes `[pre-open, newPage]`) and the layer is `consumed`:
 *   its close never calls `back()` (a navigation picked inside a sheet).
 * - When a close lands on the pre-open entry (our own `back()` or a system
 *   Back) and the layer's recorded URL differs from `location.href`, the
 *   `popstate` listener restores that URL and state with the original
 *   `replaceState`, so the filters picked while open survive the close; the
 *   router's `idx` delta is 0 (the state is the router's own, copied).
 * - Listener order. The spec asked for a capture listener to run before
 *   react-router's bubble one, but Chrome (verified, v143) runs at-target
 *   window listeners in registration order whatever the phase. The listener
 *   is registered at module load, but in this app that is NOT before the
 *   router: every importer of the shared layers sits behind a `lazy()`
 *   chunk, and `BrowserRouter` mounts statically in `App.tsx`, so the
 *   router's listener is registered first and reads the stale `location`.
 *   So after any restore one synthetic `popstate` is re-dispatched (ignored
 *   by us) and the router re-reads the restored `location`. That
 *   re-dispatch is the path production relies on.
 * - 🔴 The landed entry IS rendered first (Story 24.14b, observed in Chrome
 *   143): the router's listener runs before ours and React commits its
 *   update before ours restores the URL. A URL written over a sentinel
 *   therefore shows its OLD value for one render when that layer closes. A
 *   filter param does not care; state that decides whether a layer is open
 *   does (the reopen loop in `UnifiedShell`'s `handleSourceClose`). Such a
 *   write waits for the pop with `afterBackDismissSettled`, below.
 *
 * Known trade-off: a layer that is NOT on top and closes by its own control
 * leaves its sentinel below the top one's. That costs one extra Back later
 * and is rare (only stacked layers).
 */

type HistoryState = Record<string, unknown> | null;

interface Layer {
  id: string;
  onClose: { current: () => void };
  /** The URL the layer's entry should carry on close (`null` = unchanged). */
  url: string | null;
  /** The router state that goes with `url` (without our marker). */
  state: unknown;
  /** A push replaced the sentinel: closing must never call `back()`. */
  consumed: boolean;
}

/** Open layers, bottom → top. */
const stack: Layer[] = [];
/** Every layer whose sentinel may still be in history (open or closing). */
const live = new Map<string, Layer>();
const pendingBack = new Map<string, ReturnType<typeof setTimeout>>();
/** Layers whose own `back()` is in flight, oldest first. */
const ownPops: Layer[] = [];
let nextId = 0;

let listening = false;

/**
 * Story 24.14b — callbacks waiting for every own `back()` to settle (see
 * `afterBackDismissSettled`). Flushed on a 0ms timer once nothing is pending
 * or in flight.
 */
const settleWaiters: Array<() => void> = [];
/** The fallback timers of queued waiters (see `SETTLE_FALLBACK_MS`). */
const settleFallbacks = new Set<ReturnType<typeof setTimeout>>();

/**
 * A queued waiter runs after this long even if nothing settled: an own
 * `back()` whose `popstate` never arrives would otherwise strand every later
 * waiter (View sources, a Past topics choice, a `?docId` clear) for good.
 */
export const SETTLE_FALLBACK_MS = 500;

function isSettled(): boolean {
  return pendingBack.size === 0 && ownPops.length === 0;
}

function flushSettleWaitersIfSettled(): void {
  if (settleWaiters.length === 0 || !isSettled()) return;
  setTimeout(() => {
    // Something may have started a new close in the meantime: wait again.
    if (!isSettled()) return;
    const waiting = settleWaiters.splice(0);
    waiting.forEach((cb) => cb());
  }, 0);
}

/**
 * Story 24.14b — run `cb` once no layer's own `history.back()` is pending (a
 * close's 0ms timer) or in flight (its `popstate` not yet handled). A layer
 * opened before that pop lands would push its sentinel ON TOP of the closing
 * one, and the traversal would then land on the wrong entry — so a menu
 * action that opens another layer goes through here.
 *
 * Runs `cb` synchronously when nothing is pending; otherwise on a 0ms timer
 * after the last pending timer resolved (or was cancelled by a reopen)
 * without a `back()`, or after the last own `popstate` was handled — and in
 * any case after `SETTLE_FALLBACK_MS`. Each `cb` runs at most once.
 */
export function afterBackDismissSettled(cb: () => void): void {
  if (isSettled()) {
    cb();
    return;
  }
  // Runs at most once, whichever comes first: the settle or the fallback.
  let done = false;
  const fallback = setTimeout(() => {
    settleFallbacks.delete(fallback);
    run();
  }, SETTLE_FALLBACK_MS);
  settleFallbacks.add(fallback);
  function run(): void {
    if (done) return;
    done = true;
    clearTimeout(fallback);
    settleFallbacks.delete(fallback);
    cb();
  }
  settleWaiters.push(run);
}

function currentState(): Record<string, unknown> | null {
  const state: unknown = window.history.state;
  return state !== null && typeof state === 'object' ? (state as Record<string, unknown>) : null;
}

/** The live layer whose sentinel is the current history entry, if any. */
function currentLayer(): Layer | undefined {
  const marker = currentState()?.backDismiss;
  return typeof marker === 'string' ? live.get(marker) : undefined;
}

function asObject(state: unknown): Record<string, unknown> {
  return state !== null && typeof state === 'object' ? { ...(state as Record<string, unknown>) } : {};
}

function resolveUrl(url: string | URL | null | undefined): string {
  return url == null ? window.location.href : new URL(String(url), window.location.href).href;
}

/*
 * The hook's own writes go through the methods on History.prototype, looked
 * up at call time — never through `window.history.pushState`, which may be
 * our wrapper or a foreign one (an analytics SDK, a test spy) that delegates
 * back to ours. Since Story 24.4 the prototype methods are `initialEntry`'s
 * pass-through wrappers over the native ones (installed from `main.tsx`):
 * they note that the first load is over (a sentinel push counts as that
 * too) and then call the native method.
 */
function rawPush(state: HistoryState, url?: string | URL | null): void {
  History.prototype.pushState.call(window.history, state, '', url);
}

function rawReplace(state: unknown, url?: string | URL | null): void {
  History.prototype.replaceState.call(window.history, state, '', url);
}

function wrappedReplaceState(this: History, state: unknown, unused: string, url?: string | URL | null): void {
  const layer = currentLayer();
  if (!layer) {
    rawReplace(state, url);
    return;
  }
  layer.url = resolveUrl(url);
  layer.state = state;
  rawReplace({ ...asObject(state), backDismiss: layer.id }, url);
  void unused;
}

function wrappedPushState(this: History, state: unknown, unused: string, url?: string | URL | null): void {
  const layer = currentLayer();
  if (!layer) {
    rawPush(state as HistoryState, url);
    return;
  }
  // A navigation from inside a layer: take the sentinel's place, so Back
  // from the new page goes straight to the page the layer was opened on.
  layer.consumed = true;
  rawReplace(state, url);
  void unused;
}

/**
 * After a close lands on an entry: put the closing layer's latest URL back
 * (it was written over the sentinel, which the traversal just left). If the
 * landed entry is itself a lower layer's sentinel, keep its marker and hand
 * the URL down to that layer, so it restores again when it closes.
 * Returns whether the URL changed.
 */
function restoreAfterClose(layer: Layer): boolean {
  if (layer.consumed || layer.url === null || layer.url === window.location.href) return false;
  const below = currentLayer();
  if (below) {
    below.url = layer.url;
    below.state = layer.state;
    rawReplace({ ...asObject(layer.state), backDismiss: below.id }, layer.url);
  } else {
    rawReplace(layer.state, layer.url);
  }
  return true;
}

/** True while we re-dispatch a `popstate` of our own (ignored by us). */
let redispatching = false;

/**
 * A restore rewrote the URL inside a `popstate`. Every listener that ran
 * before ours (react-router's, when it registered first: browsers run
 * at-target listeners in registration order, capture or not) has already
 * read the stale `location` — and, in a browser, React has already rendered
 * it (see the header). Re-dispatch one `popstate` so they read the restored
 * one; react-router sees `idx` unchanged (delta 0) and only updates its
 * location.
 */
function redispatchPopState(): void {
  redispatching = true;
  try {
    window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
  } finally {
    redispatching = false;
  }
}

function onPopState(): void {
  if (redispatching) return;
  const own = ownPops.shift();
  if (own) {
    if (restoreAfterClose(own)) redispatchPopState();
    flushSettleWaitersIfSettled();
    return;
  }
  const top = stack.pop();
  if (!top) return;
  live.delete(top.id);
  const restored = restoreAfterClose(top);
  top.onClose.current();
  if (restored) redispatchPopState();
}

function ensureListener(): void {
  if (listening || typeof window === 'undefined') return;
  // Capture, and registered at module load (below). It is still usually NOT
  // the first `popstate` listener on the window (the router mounts before
  // this lazy-loaded module runs); `redispatchPopState` covers that.
  window.addEventListener('popstate', onPopState, { capture: true });
  listening = true;
}

function ensureInstalled(): void {
  if (typeof window === 'undefined') return;
  const history = window.history;
  // Install only while the instance still resolves to the prototype's method
  // (first open, or after someone restored it — e.g. a test's
  // `vi.restoreAllMocks()`); that method is the native one, or
  // `initialEntry`'s wrapper over it. Never over a
  // foreign wrapper: if a later wrapper delegates to ours, saving it as
  // "original" and wrapping it again would recurse forever. Under a
  // foreign wrapper that does not delegate to ours, router writes are not
  // tracked (the pre-review behaviour), which is the safe failure.
  if (history.replaceState === History.prototype.replaceState) {
    history.replaceState = wrappedReplaceState;
  }
  if (history.pushState === History.prototype.pushState) {
    history.pushState = wrappedPushState;
  }
  ensureListener();
}

// Register the listener as early as this module allows. It is imported by
// the shared Sheet / Dialog / AlertDialog, which the app loads in lazy
// chunks — after `BrowserRouter` has mounted and registered its own.
ensureListener();

/**
 * Close `onClose` on system Back while `open`. Pass `enabled = false` to opt
 * out (the layer then neither pushes nor listens).
 */
export function useBackDismiss(open: boolean, onClose: () => void, enabled = true): void {
  const [id] = useState(() => `back-dismiss-${++nextId}`);
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open || !enabled || typeof window === 'undefined') return;
    ensureInstalled();

    const pending = pendingBack.get(id);
    if (pending !== undefined) {
      clearTimeout(pending);
      pendingBack.delete(id);
      // That timer will never resolve now: release anything waiting on it.
      flushSettleWaitersIfSettled();
    }
    let layer = live.get(id);
    if (pending !== undefined && layer && currentState()?.backDismiss === id) {
      // StrictMode's re-run: the sentinel pushed a moment ago is still the
      // current entry. Keep it.
    } else {
      layer = { id, onClose: onCloseRef, url: null, state: null, consumed: false };
      live.set(id, layer);
      rawPush({ ...(currentState() ?? {}), backDismiss: id });
    }
    stack.push(layer);
    const opened = layer;

    return () => {
      const index = stack.indexOf(opened);
      if (index >= 0) stack.splice(index, 1);
      pendingBack.set(
        id,
        setTimeout(() => {
          pendingBack.delete(id);
          if (live.get(id) === opened) {
            live.delete(id);
            if (!opened.consumed && currentState()?.backDismiss === id) {
              ownPops.push(opened);
              window.history.back();
            }
          }
          // No `back()` (or the last one is now in flight, and its `popstate`
          // flushes instead): release anything waiting on the settle.
          flushSettleWaitersIfSettled();
        }, 0),
      );
    };
  }, [open, enabled, id]);
}

/** Test-only: reset the module state between cases and unwrap history. */
export function resetBackDismissForTests(): void {
  stack.length = 0;
  live.clear();
  pendingBack.forEach((timer) => clearTimeout(timer));
  pendingBack.clear();
  ownPops.length = 0;
  settleWaiters.length = 0;
  settleFallbacks.forEach((timer) => clearTimeout(timer));
  settleFallbacks.clear();
  if (typeof window === 'undefined') return;
  if (listening) {
    window.removeEventListener('popstate', onPopState, { capture: true });
    listening = false;
  }
  const history = window.history as unknown as Record<string, unknown>;
  for (const [name, wrapper] of [
    ['replaceState', wrappedReplaceState],
    ['pushState', wrappedPushState],
  ] as const) {
    // Our wrappers are own properties of the instance; deleting one puts
    // the prototype's native method back.
    if (Object.prototype.hasOwnProperty.call(history, name) && history[name] === wrapper) {
      delete history[name];
    }
  }
}
